import { Radio, Form, ConfigProvider, Flex } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  useSteeringFilters,
  useSteeringFiltersFormName,
} from 'business/steering/providers/steering-filters-provider';
import { Module_Enum, useGetCurrentRingQuery } from 'generated/graphql';
import 'technical/time-utils/dayjs.timezone';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';
import TemporalitySelectInput, {
  DateSelectionTypeEnum,
} from 'ui/form/temporality-select-input';

export interface SteeringFiltersProps {
  noDefaultFor?: DateSelectionTypeEnum[];
}

export const SteeringFilters = ({ noDefaultFor }: SteeringFiltersProps) => {
  const { t } = useTranslation();
  const formName = useSteeringFiltersFormName();
  const filters = useSteeringFilters();
  const [form] = Form.useForm();
  const { constructionSiteId } = usePermissions();
  const headers = useGraphqlApiHeaders(Module_Enum.Steering);

  const { data, error } = useGetCurrentRingQuery({
    variables: { constructionSiteId },
    context: {
      headers,
    },
  });

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Form
        layout={'inline'}
        form={form}
        name={formName}
        initialValues={filters}
      >
        <>
          <Form.Item
            layout={'vertical'}
            name="filterType"
            label={t('input.filterByOptions.label')}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: t('input.filterByOptions.date'),
                  value: 'date',
                },
                {
                  label: t('input.filterByOptions.ring'),
                  value: 'ring',
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              prevValues.filterType !== curValues.filterType
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const filterType = getFieldValue('filterType');

              if (filterType === 'date') {
                return (
                  <Flex>
                    <Form.Item
                      name="dateRange"
                      layout={'vertical'}
                      label={t('input.dateRange.label')}
                    >
                      <TemporalitySelectInput
                        defaultDate={dayjs().tz()}
                        noDefaultFor={noDefaultFor}
                      />
                    </Form.Item>
                  </Flex>
                );
              } else if (filterType === 'ring') {
                return (
                  <NumberRangeInputWithMessage
                    name="ringRange"
                    label={t('input.ringRange.label')}
                    leftPlaceholder={t('input.ringRange.startPlaceholder')}
                    rightPlaceholder={t('input.ringRange.endPlaceholder')}
                    message={
                      error
                        ? t('dataAnalysis.errors.currentRingNumberError')
                        : t('dataAnalysis.labels.currentRingNumber', {
                            ringNumber:
                              data?.parameter_ring_aggregate.aggregate?.max
                                ?.ringNumber,
                          })
                    }
                  />
                );
              }
              return null;
            }}
          </Form.Item>
        </>
      </Form>
    </ConfigProvider>
  );
};
